import mapboxgl from 'mapbox-gl';

import React, { useRef, useEffect, useState } from 'react'; 

import EnhancedDataLayers from './layers/EnhancedDataLayers';
import EnhancedDataHandlers from './handlers/EnhancedDataHandlers';

import AdasisLayers from './layers/AdasisLayers';
import AdasisHandlers from './handlers/AdasisHandlers';

import { completeStatus } from '../reducers/mapDataSlice';
import { useDispatch } from 'react-redux';

const slopeUrl = 'https://uploads-ssl.webflow.com/6050a76fa6a633d5d54ae714/62c69f819f238d740dd81589_slope__icon.png';
const speedLimitUrl = 'https://uploads-ssl.webflow.com/6050a76fa6a633d5d54ae714/62c894b6bf7adcf1ef0d838e_speed-limit.png';
const curvatureUrl = 'https://uploads-ssl.webflow.com/6050a76fa6a633d5d54ae714/62c69f80e9b6ae2e7825afda_curvature__icon.png';

const loadImage = (map, label, url) => {
  map.loadImage(url, (error, image) => {
    if (!error) {
      map.addImage(label, image);
    }
  });
}

function Map({data, bbox, mode, onLoad}) {
    const dispatch = useDispatch();

    const { mpp, geojson, adasis } = data;

    const mapContainer = useRef(null);
    const map = useRef(null);

    const [mapLoaded, setMapLoaded] = useState(false);
    
    const center = [11.516938813531112, 48.22972174693277];
    const zoom = 11;

    useEffect(() => {
        if (map.current) return; // initialize map only once
    
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/dark-v9',
          center: center,
          zoom: zoom
        });

        map.current.on('load', () => {
          loadImage(map.current, 'slope', slopeUrl);
          loadImage(map.current, 'speed-limit', speedLimitUrl);
          loadImage(map.current, 'curve', curvatureUrl);

          map.current.addSource('EnhancedData', { 'type': 'geojson', 'data': geojson });
          map.current.addSource('Adasis', { 'type': 'geojson', 'data': adasis });
          map.current.addSource('mpp', { 'type': 'geojson', 'data': mpp });

          map.current.on('idle', (e) => {
            dispatch(completeStatus());
          });

          map.current.on('click', (e) => {
            const features = map.current.queryRenderedFeatures(e.point);
            
            if (features.length) {
              const top = features[0];
              map.current.setFilter('EnhancedData/Edges:Highlighted:Slope', ['in', 'uuid', '']);
              map.current.setFilter('EnhancedData/Edges:Highlighted:SpeedLimit', ['in', 'uuid', '']);
              map.current.setFilter('EnhancedData/Edges:Highlighted:Curvature', ['in', 'uuid', '']);

              if (top.layer.id === 'EnhancedData/Points') {
                const layerSybtype = top.properties.type;
                const uuids = features.reduce((prev, f) => f.properties.uuid ? [...prev, f.properties.uuid] : prev, []);
                if (uuids.length) {
                  map.current.setFilter('EnhancedData/Edges:Highlighted:' + layerSybtype, ['in', 'uuid', ...uuids]);
                }
              } else if (top.layer.id === 'EnhancedData/Edges' && top.properties.uuid) {
                map.current.setFilter('EnhancedData/Edges:Highlighted:SpeedLimit', ['in', 'uuid', top.properties.uuid]);
              }
            }
          });

          for (const layer of [...EnhancedDataLayers, ...AdasisLayers]) {
            map.current.addLayer(layer);
          }

          for (const handler of [...EnhancedDataHandlers, ...AdasisHandlers]) {
            map.current.on(handler.event, handler.layer_ids, (e) => {
              handler.cb(map.current, {lngLat: e.lngLat, properties: e.features[0].properties, geometry: e.features[0].geometry });
            });
          }
    
          setMapLoaded(true);
          onLoad();
        });
    }, []);

    useEffect(() => {
      if (mapLoaded && bbox) {
        if (mode === 'EnhancedData') {
          map.current.setMaxBounds(bbox);
        } 
        
        if (mode === 'Adasis') {
          map.current.setMaxBounds(null);
          map.current.fitBounds(bbox);
        }
      }
    }, [mapLoaded, bbox])

    useEffect(() => {
        if (mapLoaded) {
          if (geojson) {
              const source = map.current.getSource('EnhancedData');
              if (source) {
                source.setData(geojson);
              }
          }

          if (mpp) {
            const source = map.current.getSource('mpp');
            if (source) {
              source.setData(mpp);
            }
          }

          if (adasis) {
            const source = map.current.getSource('Adasis');
            if (source) {
              source.setData(adasis);
            }
          }
        }
    }, [mapLoaded, mode, geojson, adasis]);

    return (
        <div ref={mapContainer} className="map-container" style={{height:'100%', width:'100%'}}/>
    );
}

export default Map;